// Ukazatel hmotnostních pásem dopravy — JEDNA implementace pro pokladnu
// (krok 2, `checkoutPage.js`) i pro stránku Doprava a platba
// (`shippingPage.js`, handover „Doprava a platba v2" §1.2: „element pásem
// z košíku tam chce určitě"). Zákazník tak vidí na obou místech tentýž prvek
// se stejnými čísly; kdo mění tarif, mění ho v `shippingModel.js` a projeví se
// v košíku i na stránce naráz.
//
// Markup zůstal beze změny z pokladny (třídy `.bs-co-wscale*`), aby se s ním
// nemusely přepisovat asserty a paritní harness — stylem je od té doby sdílená
// komponenta `scss/components/_weight-scale.scss` (dřív scope `_checkout.scss`).
//
// Co modul NEDĚLÁ: nepočítá hmotnost (tu dodá volající z dataLayeru nebo
// z volby zákazníka) a nerozhoduje, jestli se má kreslit — pokladna ho vynechá
// bez váhy v datech i bez běžné doručované metody v nabídce.

import { icon } from './icon.js';
import { money } from './money.js';
import {
  WEIGHT_BANDS,
  WEIGHT_SCALE_TITLE,
  WEIGHT_SCALE_BOX,
  WEIGHT_SCALE_ADDR,
  WEIGHT_SCALE_OVER_LABEL,
  weightBandRows,
  formatKg,
} from './shippingModel.js';
import L from '../locale/cs/weightScale.js';

function node(tag, className, text) {
  const n = document.createElement(tag);
  if (className) n.className = className;
  if (text != null) n.textContent = text;
  return n;
}

export const priceText = (value) => money(value);

// Cena výdejního místa v pásmu. V akci na dopravu (#322) platí akční cena jen
// pro jednoho dopravce → „od 19 Kč", ne holých „19 Kč" (druhý dopravce stojí 69).
function boxText(row) {
  return row.promo ? L.promoFrom(priceText(row.promo.price)) : priceText(row.box);
}

/**
 * Řádek aktuálního pásma s cenami („10–20 kg | Výdejní místo 99 Kč · Na adresu
 * 139 Kč"). V pokladně ho vidí jen mobil (ceny tam jsou pod segmenty), na
 * stránce je to výsledek kalkulačky — na desktopu v ovládacím panelu vlevo,
 * na mobilu uvnitř škály (handover §1.2).
 *
 * @param {object} band pásmo z `bandOf()`
 * @param {object} [opts]
 * @param {boolean} [opts.free] v pásmu platí doprava zdarma (nad prahem) →
 *   místo ceny výdejního místa „Zdarma" zeleně
 * @param {number} [opts.overPrice] cena přepravy pro nadměrné balíky —
 *   nad posledním pásmem ji zná ceník stránky, pokladna ne (nechá jen popisek)
 * @param {string} [className] třída navíc (stránka si řádek pozicuje sama)
 */
export function weightCurRow(band, opts, className) {
  const o = opts || {};
  const rows = weightBandRows();
  const cur = node('div', 'bs-co-wscale__cur' + (className ? ' ' + className : ''));
  cur.appendChild(node('b', null, band.label));
  const curP = node('span', 'bs-co-wscale__cur-p');
  const row = rows[band.index];
  if (band.over || !row) {
    curP.appendChild(document.createTextNode(WEIGHT_SCALE_OVER_LABEL));
    const over = priceText(o.overPrice);
    if (over) {
      curP.appendChild(document.createTextNode(' '));
      curP.appendChild(node('b', null, over));
    }
  } else {
    curP.appendChild(document.createTextNode(WEIGHT_SCALE_BOX + ' '));
    curP.appendChild(
      o.free
        ? node('b', 'bs-co-wscale__free', L.free)
        : node('b', row.promo ? 'bs-co-wscale__promo-price' : null, boxText(row))
    );
    curP.appendChild(document.createTextNode(' · ' + WEIGHT_SCALE_ADDR + ' '));
    curP.appendChild(node('b', null, priceText(row.addr)));
  }
  cur.appendChild(curP);
  return cur;
}

/**
 * Celý ukazatel: titulek s ikonou váhy, bublina s hmotností nad segmenty pásem
 * (aktuální červeně), ceny pod segmenty, řádek aktuálního pásma (mobil)
 * a poznámka. Nad posledním pásmem stojí bublina na kraji (clamp 2–98 %)
 * a žádný segment není aktivní.
 *
 * @param {object} def
 * @param {number} def.kg hmotnost zásilky
 * @param {object} def.band pásmo z `bandOf(kg)`
 * @param {string|null} [def.note] poznámka pod škálou (volající ví, jestli
 *   jsou v nabídce výdejní místa — bez nich by slibovala metodu, která
 *   v objednávce není)
 * @param {boolean} [def.cur] vykreslit řádek aktuálního pásma (default true)
 * @param {boolean} [def.free] doprava zdarma v aktuálním pásmu (řádek pásma)
 * @param {number} [def.overPrice] cena nad posledním pásmem (řádek pásma)
 * @returns {HTMLElement} `.bs-co-wscale`
 */
export function buildWeightScale(def) {
  const el = node('div', 'bs-co-wscale');

  const title = node('div', 'bs-co-wscale__title');
  title.appendChild(icon('scale'));
  title.appendChild(node('span', null, WEIGHT_SCALE_TITLE));
  el.appendChild(title);

  const scale = node('div', 'bs-co-wscale__scale');
  const last = WEIGHT_BANDS[WEIGHT_BANDS.length - 1];
  const ptr = node('span', 'bs-co-wscale__ptr');
  ptr.style.left = Math.min(98, Math.max(2, (def.kg / last) * 100)) + '%';
  ptr.appendChild(node('span', 'bs-co-wscale__bubble', formatKg(def.kg)));
  ptr.appendChild(node('span', 'bs-co-wscale__tip'));
  scale.appendChild(ptr);

  const segs = node('div', 'bs-co-wscale__segs');
  for (const b of weightBandRows()) {
    const seg = node('div', 'bs-co-wscale__seg' + (b.index === def.band.index ? ' is-cur' : ''));
    seg.appendChild(node('span', 'bs-co-wscale__bar'));
    seg.appendChild(node('span', 'bs-co-wscale__lab', b.label));
    const p = node('span', 'bs-co-wscale__prices');
    p.appendChild(document.createTextNode(WEIGHT_SCALE_BOX + ' '));
    p.appendChild(node('b', b.promo ? 'bs-co-wscale__promo-price' : null, boxText(b)));
    p.appendChild(node('br'));
    p.appendChild(document.createTextNode(WEIGHT_SCALE_ADDR + ' '));
    p.appendChild(node('b', null, priceText(b.addr)));
    seg.appendChild(p);
    segs.appendChild(seg);
  }
  scale.appendChild(segs);
  el.appendChild(scale);

  if (def.cur !== false) {
    el.appendChild(weightCurRow(def.band, { free: def.free, overPrice: def.overPrice }));
  }
  if (def.note) el.appendChild(node('div', 'bs-co-wscale__note', def.note));
  // akce na dopravu (#322): věta, PROČ je výdejní místo „od" akční ceny
  const promo = (weightBandRows()[0] || {}).promo;
  if (promo) el.appendChild(node('div', 'bs-co-wscale__note bs-co-wscale__promo', L.promoNote(promo.carrier, priceText(promo.price), promo.maxKg, promo.until)));
  return el;
}
